import React from "react";
import { ArrowLeft } from "lucide-react";

import { cn } from "../../lib/utils";
import { ZAR8_YIELD_FLOW_TYPES } from "../../utils/zar8LockFlow";

export const ZAR8_YIELD_FLOW_BACKGROUND =
  "linear-gradient(180deg, rgba(248, 248, 248, 1) 0%, rgba(255, 255, 255, 1) 100%)";
export const ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND =
  "linear-gradient(70.19deg, #57249C 10.66%, #EB3366 113.47%)";

const ZAR8_FIGMA_CIRCLE = "/figma/zar8-yield/zar8-circle.svg";
const ZAR8_FIGMA_MARK = "/figma/zar8-yield/zar8-mark-small.png";

export function StepShell({ children }) {
  return (
    <div
      className="min-h-[calc(100vh-96px)] px-5 pb-12 pt-7 md:px-8 xl:px-[52px] xl:pt-9"
      style={{ background: ZAR8_YIELD_FLOW_BACKGROUND }}
    >
      <div className="mx-auto max-w-[739px]">{children}</div>
    </div>
  );
}

export function FlowModePill({ currentMode, onChange }) {
  return (
    <div className="mt-10 rounded-full border border-[#EBE9EF] p-2">
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onChange?.(ZAR8_YIELD_FLOW_TYPES.lock)}
          className={cn(
            "flex h-12 items-center justify-center rounded-full text-sm font-semibold leading-5 transition-colors",
            currentMode === ZAR8_YIELD_FLOW_TYPES.lock
              ? "bg-[#33125F] text-white"
              : "text-[#9D96AF] hover:bg-[#FAF9FC]"
          )}
        >
          Lock
        </button>
        <button
          type="button"
          onClick={() => onChange?.(ZAR8_YIELD_FLOW_TYPES.unlock)}
          className={cn(
            "flex h-12 items-center justify-center rounded-full text-sm font-semibold leading-5 transition-colors",
            currentMode === ZAR8_YIELD_FLOW_TYPES.unlock
              ? "bg-[#33125F] text-white"
              : "text-[#9D96AF] hover:bg-[#FAF9FC]"
          )}
        >
          Unlock
        </button>
      </div>
    </div>
  );
}

export function WizardCard({
  title,
  onCancel,
  onHeaderBack,
  children,
  footer,
  className,
  disableCancel = false,
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[24px] border border-[#EBE9EF] bg-white pt-10",
        className
      )}
    >
      {onHeaderBack ? (
        <div className="absolute left-[27px] top-[23px]">
          <BackButton onClick={onHeaderBack} />
        </div>
      ) : null}
      {!disableCancel ? (
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-6 top-6 rounded-full px-4 py-3 text-sm font-semibold leading-5 text-[#EB3366] transition-colors hover:bg-[#FFF5F8]"
        >
          Cancel
        </button>
      ) : null}
      <div className="px-6 pb-10 sm:px-[33px]">
        <h1 className="text-center text-xl font-semibold leading-7 text-[#57249C]">
          {title}
        </h1>
        {children}
      </div>
      {footer}
    </div>
  );
}

export function YieldAssetBadge({ assetIcon }) {
  if (!assetIcon) {
    return (
      <span className="relative block h-10 w-10 shrink-0">
        <img
          src={ZAR8_FIGMA_CIRCLE}
          alt=""
          aria-hidden="true"
          className="block h-10 w-10"
        />
        <img
          src={ZAR8_FIGMA_MARK}
          alt=""
          aria-hidden="true"
          className="absolute left-[11px] top-[9px] h-[22px] w-[17px] object-contain"
        />
      </span>
    );
  }

  return (
    <img
      src={assetIcon}
      alt=""
      aria-hidden="true"
      className="h-10 w-10 rounded-full object-cover"
    />
  );
}

export function FieldLabel({ children }) {
  return (
    <p className="text-sm font-normal leading-5 text-[#6D6584]">{children}</p>
  );
}

export function SecondaryField({ children, className }) {
  return (
    <div
      className={cn(
        "flex min-h-[88px] items-center rounded-2xl border border-[#CECBD7] bg-white px-6",
        className
      )}
    >
      {children}
    </div>
  );
}

export function BannerField({ children }) {
  return (
    <div className="flex min-h-[88px] items-center rounded-2xl bg-[rgba(235,233,239,0.3)] px-6">
      {children}
    </div>
  );
}

export function BackButton({ onClick, ariaLabel = "Back" }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="flex h-12 w-12 items-center justify-center rounded-full border border-[#EBE9EF] text-[#6D6584] transition-colors hover:bg-[#FAF9FC]"
    >
      <ArrowLeft className="h-4 w-4" />
    </button>
  );
}

export function ContinueButton({ children, onClick, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-12 w-full rounded-full px-6 text-sm font-semibold leading-5 text-white transition-opacity sm:w-[302px]",
        disabled && "cursor-not-allowed opacity-40"
      )}
      style={{ background: ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND }}
    >
      {children}
    </button>
  );
}

export function LargeGhostButton({ children, onClick, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-12 w-full rounded-full border border-[#EBE9EF] px-6 text-sm font-semibold leading-5 text-[#EB3366] transition-colors hover:bg-[#FFF5F8]",
        disabled && "cursor-not-allowed opacity-40"
      )}
    >
      {children}
    </button>
  );
}

export function LargeGradientButton({ children, onClick, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-12 w-full rounded-full px-6 text-sm font-semibold leading-5 text-white transition-opacity",
        disabled && "cursor-not-allowed opacity-40"
      )}
      style={{ background: ZAR8_YIELD_PRIMARY_BUTTON_BACKGROUND }}
    >
      {children}
    </button>
  );
}
